/**
 * Decorations (D291, D331): building, roof, path, fence, hedge, gate and vehicle styles, and
 * the hands' clothes. They restyle the world and change no numbers. Bought with Trimmings (D331) and kept for
 * good, across festivals and generations. Each kind's first entry in `content.decorations` is its
 * default: free, owned by every world, and shown on a world that has chosen nothing.
 *
 * A decoration with `festivals` opens once that many festivals have been held. One with `event`
 * is seasonal: it can be bought only while that weekly event runs (D304), and comes back each
 * time the event does. Once bought, either is the world's for good.
 */
import type { IdleContent, IdleDecorationKind, IdleDecorationT } from '@hedgerow/data';
import { idleEventNow } from './daily.ts';
import { checkInIdle, type IdleResult } from './world.ts';
import type { IdleWorldState } from './state.ts';
import { idleTrimmings } from './trimmings.ts';

export const IDLE_DECORATION_KINDS: IdleDecorationKind[] = [
  'building',
  'roof',
  'path',
  'fence',
  'hedge',
  'gate',
  'vehicle',
  'worker',
];

/** A kind's default: its first decoration in the content. */
export function idleDefaultDecoration(
  content: IdleContent,
  kind: IdleDecorationKind,
): IdleDecorationT | undefined {
  return content.decorations.find((d) => d.kind === kind);
}

/** True when the world may show it: a default, or bought. */
export function idleDecorationOwned(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
): boolean {
  const d = content.decorations.find((x) => x.id === id);
  if (!d) return false;
  return (
    idleDefaultDecoration(content, d.kind)?.id === id ||
    !!state.permanent.decorations?.owned.includes(id)
  );
}

/** D331: festivals still to hold before a decoration can be bought. 0 when it is open. */
export function idleDecorationFestivalsNeeded(state: IdleWorldState, d: IdleDecorationT): number {
  return Math.max(0, (d.festivals ?? 0) - state.permanent.festivals.length);
}

/** D331: true when a decoration can be bought at `now` as far as its season goes. */
export function idleDecorationInSeason(
  state: IdleWorldState,
  content: IdleContent,
  d: IdleDecorationT,
  now: number,
): boolean {
  return d.event === undefined || idleEventNow(state, content, now).event.id === d.event;
}

/** The decoration shown for each kind: the one chosen, or the default. */
export function idleDecorations(
  state: IdleWorldState,
  content: IdleContent,
): Record<IdleDecorationKind, string> {
  const out = {} as Record<IdleDecorationKind, string>;
  for (const k of IDLE_DECORATION_KINDS) {
    const chosen = state.permanent.decorations?.chosen[k];
    const ok = chosen !== undefined && idleDecorationOwned(state, content, chosen);
    out[k] = ok ? chosen : (idleDefaultDecoration(content, k)?.id ?? '');
  }
  return out;
}

/**
 * D331: the world as it would look with `preview` shown for their kinds, for trying a decoration
 * on before it is bought. Looks only: never stored, never acted on.
 */
export function idlePreviewDecorations(
  state: IdleWorldState,
  content: IdleContent,
  preview: Partial<Record<IdleDecorationKind, string>>,
): IdleWorldState {
  const ids = Object.values(preview).filter((id): id is string =>
    content.decorations.some((d) => d.id === id),
  );
  if (ids.length === 0) return state;
  const had = state.permanent.decorations;
  const chosen = { ...(had?.chosen ?? {}) };
  for (const id of ids) chosen[content.decorations.find((d) => d.id === id)!.kind] = id;
  const owned = [...new Set([...(had?.owned ?? []), ...ids])];
  return { ...state, permanent: { ...state.permanent, decorations: { owned, chosen } } };
}

/** Choose a decoration the world owns for its kind. */
function choose(state: IdleWorldState, d: IdleDecorationT, owned: string[]): IdleWorldState {
  const had = state.permanent.decorations;
  return {
    ...state,
    permanent: {
      ...state.permanent,
      decorations: { owned, chosen: { ...(had?.chosen ?? {}), [d.kind]: d.id } },
    },
  };
}

/** D331: buy a decoration with Trimmings, and show it at once. */
export function buyIdleDecoration(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const d = content.decorations.find((x) => x.id === id);
  if (!d) return { ok: false, reason: 'unknown_decoration' };
  if (idleDecorationOwned(s, content, id)) return { ok: false, reason: 'already_bought' };
  const festivals = idleDecorationFestivalsNeeded(s, d);
  if (festivals > 0) return { ok: false, reason: 'needs_festivals', needs: festivals };
  if (!idleDecorationInSeason(s, content, d, now)) return { ok: false, reason: 'out_of_season' };
  const have = idleTrimmings(s);
  if (have < d.cost) return { ok: false, reason: 'not_enough_trimmings', needs: d.cost - have };
  const owned = [...(s.permanent.decorations?.owned ?? []), id];
  const paid = { ...s, permanent: { ...s.permanent, trimmings: have - d.cost } };
  return { ok: true, state: choose(paid, d, owned) };
}

/** Show a decoration the world owns (a default, or one bought) for its kind. */
export function setIdleDecoration(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const d = content.decorations.find((x) => x.id === id);
  if (!d) return { ok: false, reason: 'unknown_decoration' };
  if (!idleDecorationOwned(s, content, id)) return { ok: false, reason: 'not_owned' };
  return { ok: true, state: choose(s, d, s.permanent.decorations?.owned ?? []) };
}
